import React, { useEffect, useState } from 'react'
import DashboardHeader from "../layouts/dashboard_header";
import SideMenu from "../layouts/dashboard_menu";
import DashboardFooter from "../layouts/dashboard_footer";
import { buildApiUrl, useBaseUrl } from "../../utils/utils";
import localforage from "localforage";
import Swal from "sweetalert2";

interface StaffRow {
    id: number;
    fname: string;
    mname: string;
    lname: string;
    email: string;
    title: string;
    active: boolean;
}

const SuspendStaff: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [staff, setStaff] = useState<StaffRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [updatingId, setUpdatingId] = useState<number | null>(null);

    useEffect(() => {
        document.title = "Manage Staff Status - Uzima Healthcare";
        localforage.getItem("clinic_id").then((cid: any) => setClinicId(cid ? Number(cid) : null));
    }, []);

    const loadStaff = async () => {
        if (!clinicId) return;
        setLoading(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/staff/status-directory?clinic_id=${clinicId}&search=${encodeURIComponent(search)}`));
            const data = await res.json();
            if (data.status === 'success') setStaff(data.data || []);
        } catch { /* leave list as-is on failure */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadStaff(); }, [clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleStatus = async (member: StaffRow) => {
        const nextActive = !member.active;
        const confirm = await Swal.fire({
            title: nextActive ? 'Reactivate this staff member?' : 'Suspend this staff member?',
            text: nextActive ? 'They will regain access immediately.' : 'They will lose access to the platform immediately.',
            icon: 'warning', showCancelButton: true, confirmButtonColor: nextActive ? '#0f766e' : '#dc3545',
            confirmButtonText: nextActive ? 'Yes, reactivate' : 'Yes, suspend',
        });
        if (!confirm.isConfirmed) return;

        setUpdatingId(member.id);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/staff/${member.id}/status`), {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ active: nextActive }),
            });
            if (res.ok) {
                Swal.fire({ icon: 'success', title: nextActive ? 'Reactivated' : 'Suspended', timer: 1200, showConfirmButton: false });
                loadStaff();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update staff status.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error while updating staff status.' });
        } finally {
            setUpdatingId(null);
        }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className={"vertical-overlay"}></div>
            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0">Manage Staff Status</h4>
                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a href="/institution/staff">Staff</a></li>
                                            <li className="breadcrumb-item active">Suspend / Reactivate</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header">
                                        <div className="row g-3 align-items-center">
                                            <div className="col-md-6">
                                                <input type="text" className="form-control" placeholder="Search staff by name or email..."
                                                       value={search}
                                                       onChange={(e) => setSearch(e.target.value)}
                                                       onKeyDown={(e) => { if (e.key === 'Enter') loadStaff(); }} />
                                            </div>
                                            <div className="col-md-3">
                                                <button className="btn btn-primary w-100" onClick={loadStaff}>
                                                    <i className="ri-search-line me-1"></i>Search
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        {loading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                                            </div>
                                        ) : staff.length === 0 ? (
                                            <div className="text-center py-5 text-muted">No staff found for this facility.</div>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-hover align-middle mb-0">
                                                    <thead>
                                                        <tr><th>Name</th><th>Email</th><th>Title</th><th>Status</th><th>Action</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {staff.map(member => (
                                                            <tr key={member.id}>
                                                                <td>{member.fname} {member.mname} {member.lname}</td>
                                                                <td>{member.email}</td>
                                                                <td>{member.title || '—'}</td>
                                                                <td>
                                                                    <span className={`badge ${member.active ? 'bg-success' : 'bg-danger'}`}>
                                                                        {member.active ? 'Active' : 'Suspended'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        className={`btn btn-sm ${member.active ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                                                        disabled={updatingId === member.id}
                                                                        onClick={() => toggleStatus(member)}>
                                                                        {updatingId === member.id ? 'Updating...' : (member.active ? 'Suspend' : 'Reactivate')}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <DashboardFooter />
            </div>
        </>
    )
}

export default SuspendStaff
